import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'primereact/tooltip';
import { COMMON_TEXT } from '@/helpers/common-text';
import { createAuthenticatedApiClient } from '@/helpers/api-client';
import { EmployeelocationsApi } from 'src/openapi';
import './FollowAllButton.less';

class FollowAllButton extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
    };
  }

  handleFollowAll = async () => {
    const { employeeList, followedEmployeeIds, onFollowChange, showToast } = this.props;

    if (!employeeList || employeeList.length === 0) {
      return;
    }

    // Calculate which employees need to be followed
    const unfollowedEmployeeIds = employeeList
      .filter(emp => !followedEmployeeIds.has(parseInt(emp.employee_id)))
      .map(emp => parseInt(emp.employee_id));

    if (unfollowedEmployeeIds.length === 0) {
      return;
    }

    this.setState({ isLoading: true });

    try {
      const employeeLocationsApi = createAuthenticatedApiClient(EmployeelocationsApi);

      await employeeLocationsApi.followEmployees({
        apiEmployeeLocationFollowsRequest: {
          employeeIds: unfollowedEmployeeIds,
        },
      });

      // Update state for all newly followed employees
      if (onFollowChange) {
        unfollowedEmployeeIds.forEach(employeeId => {
          onFollowChange(employeeId, true);
        });
      }

      if (showToast) {
        showToast({
          severity: 'success',
          summary: `${unfollowedEmployeeIds.length}人をフォローしました`,
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Error following all employees:', error);

      if (showToast) {
        showToast({
          severity: 'error',
          summary: 'エラーが発生しました',
          detail: '全員フォローに失敗しました',
          life: 3000,
        });
      }
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleUnfollowAll = async () => {
    const { employeeList, followedEmployeeIds, onFollowChange, showToast } = this.props;

    if (!employeeList || employeeList.length === 0) {
      return;
    }

    // Calculate which employees in the current list need to be unfollowed
    const followedEmployeeIdsInList = employeeList
      .filter(emp => followedEmployeeIds.has(parseInt(emp.employee_id)))
      .map(emp => parseInt(emp.employee_id));

    if (followedEmployeeIdsInList.length === 0) {
      return;
    }

    this.setState({ isLoading: true });

    try {
      const employeeLocationsApi = createAuthenticatedApiClient(EmployeelocationsApi);

      // Unfollow each employee individually using Promise.all
      await Promise.all(
        followedEmployeeIdsInList.map(employeeId =>
          employeeLocationsApi.unfollowEmployee({ employeeId })
        )
      );

      // Update state for unfollowed employees
      if (onFollowChange) {
        followedEmployeeIdsInList.forEach(employeeId => {
          onFollowChange(employeeId, false);
        });
      }

      if (showToast) {
        showToast({
          severity: 'success',
          summary: `${followedEmployeeIdsInList.length}人のフォローを解除しました`,
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Error unfollowing all employees:', error);

      if (showToast) {
        showToast({
          severity: 'error',
          summary: 'エラーが発生しました',
          detail: '全員フォロー解除に失敗しました',
          life: 3000,
        });
      }
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { employeeList, followedEmployeeIds, className } = this.props;
    const { isLoading } = this.state;

    if (!employeeList || employeeList.length === 0) {
      return null;
    }

    // Calculate if all employees are followed
    const allEmployeeIds = employeeList.map(emp => parseInt(emp.employee_id));
    const allFollowed = allEmployeeIds.every(id => followedEmployeeIds.has(id));

    // Determine tooltip and action
    const tooltipText = allFollowed ? COMMON_TEXT.UNFOLLOW_ALL : COMMON_TEXT.FOLLOW_ALL;
    const handleClick = allFollowed ? this.handleUnfollowAll : this.handleFollowAll;
    const icon = allFollowed ? 'pi pi-star-fill' : 'pi pi-star';
    const modeClass = allFollowed ? 'following' : ''; // Use 'following' to match FollowButton

    return (
      <div className={`follow-all-button-container ${className || ''}`}>
        <div className="spacer" />
        <Tooltip target=".follow-all-btn" content={tooltipText} position="top" />
        <div
          className={`follow-all-btn follow-button ${modeClass} ${isLoading ? 'loading' : ''}`} // Reusing follow-button class for base styles might be possible, but let's stick to less
          onClick={handleClick}
        >
          <i className={icon + (isLoading ? ' pi-spin pi-spinner' : '')} />
        </div>
        <div className="status-spacer" />
      </div>
    );
  }
}

FollowAllButton.propTypes = {
  employeeList: PropTypes.array.isRequired,
  followedEmployeeIds: PropTypes.instanceOf(Set).isRequired,
  onFollowChange: PropTypes.func,
  showToast: PropTypes.func,
  className: PropTypes.string,
};

FollowAllButton.defaultProps = {
  onFollowChange: null,
  showToast: null,
  className: '',
};

export default FollowAllButton;
